import { notify } from '@/lib/toast';
import { useState, useEffect, useRef } from 'react';
import { RecordLink } from '@/components/records/RecordLink';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save, Users, History, Star, Trophy, Search, X, TrendingUp,
  Gift, Settings2, ChevronRight, SlidersHorizontal, Medal, Plus, Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { PageHeader } from '@/components/ui/page-header';
import { TabBar } from '@/components/ui/tab-bar';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PaginationBar } from '@/components/ui/pagination-bar';

type Tab = 'leaderboard' | 'settings';

interface LoyaltyTier {
  name: string;
  minPoints: number;
  multiplier: number;
  badge: string;
  color: string;
}

/** Numeric fields may arrive as Decimal strings and are edited as strings in the form. */
type NumericField = number | string;

interface LoyaltySettingsResponse {
  isEnabled: boolean;
  earnRatePoints: NumericField;
  earnRatePKR: NumericField;
  redeemRate: NumericField;
  minRedeemPoints: NumericField;
  expiryDays?: NumericField | null;
  tiers?: LoyaltyTier[] | null;
  referralEnabled?: boolean | null;
  referralBonusReferrer?: NumericField | null;
  referralBonusReferee?: NumericField | null;
}

interface LoyaltySettingsForm {
  isEnabled: boolean;
  earnRatePoints: NumericField;
  earnRatePKR: NumericField;
  redeemRate: NumericField;
  minRedeemPoints: NumericField;
  expiryDays: string;
  tiers: LoyaltyTier[];
  referralEnabled: boolean;
  referralBonusReferrer: NumericField;
  referralBonusReferee: NumericField;
}

interface LoyaltyCustomer {
  id: string;
  name?: string;
  phone?: string | null;
  loyaltyPoints?: number | null;
  loyaltyPointsEarned?: number | null;
}

interface LoyaltyCustomersResponse {
  data?: LoyaltyCustomer[];
  meta?: { total?: number };
  total?: number;
}

interface LoyaltyHistoryEntry {
  id: string;
  type: string;
  points: number;
  balanceAfter: number;
  createdAt: string;
  notes?: string | null;
  sale?: { id?: string; invoiceNumber: string } | null;
}

interface LoyaltyHistoryResponse {
  transactions?: LoyaltyHistoryEntry[];
  total?: number;
}

interface LoyaltyAdjustResponse {
  newBalance: number;
}

export function LoyaltySettingsPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission('loyalty:manage') || hasPermission('loyalty:*');
  const [tab, setTab] = useState<Tab>('leaderboard');

  // --- Settings state ---
  const [settingsForm, setSettingsForm] = useState<LoyaltySettingsForm | null>(null);

  // --- Leaderboard / customer search ---
  const [customerSearch, setCustomerSearch] = useState('');
  const [lbPage, setLbPage] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState<LoyaltyCustomer | null>(null);
  const [histPage, setHistPage] = useState(1);
  const [adjustPoints, setAdjustPoints] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [showAdjust, setShowAdjust] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const qc = useQueryClient();

  const patchForm = (patch: Partial<LoyaltySettingsForm>) =>
    setSettingsForm((f) => (f ? { ...f, ...patch } : f));
  const patchTier = (idx: number, patch: Partial<LoyaltyTier>) =>
    setSettingsForm((f) => {
      if (!f) return f;
      const tiers = [...(f.tiers ?? [])];
      tiers[idx] = { ...tiers[idx], ...patch };
      return { ...f, tiers };
    });

  // Settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['loyalty-settings'],
    queryFn: () => api.get<LoyaltySettingsResponse>('/loyalty/settings').then(r => r.data),
  });

  useEffect(() => {
    if (settings && !settingsForm) {
      setSettingsForm({
        isEnabled: settings.isEnabled,
        earnRatePoints: settings.earnRatePoints,
        earnRatePKR: settings.earnRatePKR,
        redeemRate: settings.redeemRate,
        minRedeemPoints: settings.minRedeemPoints,
        expiryDays: settings.expiryDays && Number(settings.expiryDays) > 0
          ? String(settings.expiryDays)
          : '',
        tiers: settings.tiers ?? [],
        referralEnabled: settings.referralEnabled ?? false,
        referralBonusReferrer: settings.referralBonusReferrer ?? 50,
        referralBonusReferee: settings.referralBonusReferee ?? 25,
      });
    }
  }, [settings, settingsForm]);

  // Leaderboard — all customers sorted by loyalty points desc
  const { data: lbData, isLoading: lbLoading } = useQuery({
    queryKey: ['loyalty-leaderboard', lbPage, customerSearch],
    queryFn: () => api.get<LoyaltyCustomersResponse>('/customers', {
      params: { page: lbPage, limit: 20, sortBy: 'loyaltyPoints', sortOrder: 'desc', search: customerSearch || undefined },
    }).then(r => r.data),
  });

  // Selected customer history
  const { data: histData, isLoading: histLoading } = useQuery({
    queryKey: ['loyalty-history', selectedCustomer?.id, histPage],
    queryFn: () => api.get<LoyaltyHistoryResponse>(`/loyalty/customers/${selectedCustomer?.id}/history`, { params: { page: histPage, limit: 15 } }).then(r => r.data),
    enabled: !!selectedCustomer,
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!settingsForm) return Promise.reject(new Error('Settings not loaded'));
      return api.patch('/loyalty/settings', {
      ...settingsForm,
      earnRatePoints: Number(settingsForm.earnRatePoints),
      earnRatePKR: Number(settingsForm.earnRatePKR),
      redeemRate: Number(settingsForm.redeemRate),
      minRedeemPoints: Number(settingsForm.minRedeemPoints),
      expiryDays: settingsForm.expiryDays ? Number(settingsForm.expiryDays) : null,
      tiers: settingsForm.tiers ?? [],
      referralEnabled: settingsForm.referralEnabled ?? false,
      referralBonusReferrer: Number(settingsForm.referralBonusReferrer ?? 50),
      referralBonusReferee: Number(settingsForm.referralBonusReferee ?? 25),
      });
    },
    onSuccess: () => {
      toast.success(t('loyaltySettingsSaved'));
      qc.invalidateQueries({ queryKey: ['loyalty-settings'] });
    },
    onError: (err) => notify.error(err, t('loyaltyFailedToSave')),
  });

  const adjustMutation = useMutation({
    mutationFn: () => api.post<LoyaltyAdjustResponse>(`/loyalty/customers/${selectedCustomer?.id}/adjust`, {
      points: parseInt(adjustPoints),
      notes: adjustReason || undefined,
    }),
    onSuccess: (res) => {
      toast.success(t('loyaltyPointsAdjusted', { balance: res.data.newBalance }));
      qc.invalidateQueries({ queryKey: ['loyalty-history', selectedCustomer?.id] });
      qc.invalidateQueries({ queryKey: ['loyalty-leaderboard'] });
      setAdjustPoints('');
      setAdjustReason('');
      setShowAdjust(false);
      // Update local customer balance
      if (selectedCustomer) setSelectedCustomer((c) => (c ? { ...c, loyaltyPoints: res.data.newBalance } : c));
    },
    onError: (err) => notify.error(err, t('loyaltyFailedToAdjust')),
  });

  const { data: programSummary } = useQuery<{
    members: number; activeMembers: number; pointsOutstanding: number; cashValueOutstanding: number;
    pointsEarned: number; pointsRedeemed: number; pointsExpired: number;
  }>({
    queryKey: ['loyalty-summary'],
    queryFn: () => api.get('/loyalty/summary').then((r) => r.data),
  });

  const history = histData?.transactions ?? [];
  const histTotal = histData?.total ?? 0;
  const customers: LoyaltyCustomer[] = lbData?.data ?? [];
  // GET /customers returns PaginatedResponse { data, meta: { total } } (T-224)
  const lbTotal: number = lbData?.meta?.total ?? lbData?.total ?? 0;

  // Summary stats derived from leaderboard
  const totalPoints = customers.reduce((s: number, c: LoyaltyCustomer) => s + (c.loyaltyPoints ?? 0), 0);
  const activeMembers = customers.filter((c: LoyaltyCustomer) => (c.loyaltyPoints ?? 0) > 0).length;
  const redeemRate = Number(settingsForm?.redeemRate ?? 0.01);
  const totalCashValue = totalPoints * redeemRate;

  const rankIcon = (i: number) => {
    if (i === 0) return <Trophy className="h-4 w-4 text-warning-text" />;
    if (i === 1) return <Medal className="h-4 w-4 text-muted-foreground" />;
    if (i === 2) return <Medal className="h-4 w-4 text-warning-text" />;
    return <span className="text-xs font-bold text-muted-foreground w-4 text-center">{i + 1}</span>;
  };

  const DEFAULT_TIERS = [
    { name: 'Bronze', minPoints: 0, multiplier: 1, badge: '🥉', color: '#cd7f32' },
    { name: 'Silver', minPoints: 2000, multiplier: 1.5, badge: '🥈', color: '#c0c0c0' },
    { name: 'Gold', minPoints: 5000, multiplier: 2, badge: '🥇', color: '#ffd700' },
  ];

  const resolveTier = (pointsEarned: number, tiers: LoyaltyTier[]) => {
    if (!tiers || tiers.length === 0) return null;
    const sorted = [...tiers].sort((a, b) => b.minPoints - a.minPoints);
    return sorted.find((t) => pointsEarned >= t.minPoints) ?? null;
  };

  const activeTiers = settingsForm?.tiers?.length ? settingsForm.tiers : [];
  const customerTier = selectedCustomer
    ? resolveTier(selectedCustomer.loyaltyPointsEarned ?? 0, activeTiers)
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('loyaltyTitle')}
        subtitle={t('loyaltySubtitle')}
      />

      <TabBar
        tabs={[
          { key: 'leaderboard', label: t('loyaltyCustomersTab'), icon: Trophy },
          { key: 'settings', label: t('loyaltySettingsTab'), icon: SlidersHorizontal },
        ]}
        activeTab={tab}
        onTabChange={(key) => setTab(key as Tab)}
      />

      {/* ── Stats row ── */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { icon: Users, label: t('loyaltyTotalMembers'), value: programSummary?.members ?? lbTotal, color: 'from-primary to-primary/80', shadow: 'shadow-primary/20' },
          { icon: Star, label: t('loyaltyActiveMembersLabel'), value: programSummary?.activeMembers ?? activeMembers, color: 'from-warning to-warning/80', shadow: 'shadow-warning/20' },
          { icon: TrendingUp, label: t('loyaltyPointsOutstanding', { defaultValue: 'Points outstanding (all members)' }), value: (programSummary?.pointsOutstanding ?? totalPoints).toLocaleString(), color: 'from-success to-success/80', shadow: 'shadow-success/20' },
          { icon: Gift, label: t('loyaltyIssuedRedeemed', { defaultValue: 'Points issued / redeemed (all time)' }), value: programSummary ? `${programSummary.pointsEarned.toLocaleString()} / ${programSummary.pointsRedeemed.toLocaleString()}` : formatCurrency(totalCashValue), color: 'from-destructive to-destructive/80', shadow: 'shadow-destructive/20' },
        ].map(({ icon: Icon, label, value, color, shadow }, i) => (
          <Card key={i} className="hover:-translate-y-0.5 transition-all duration-200">
            <CardContent className="flex items-center gap-3 p-4">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${color} text-primary-foreground shadow-md ${shadow}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold leading-tight truncate">{value}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Main content ── */}
      {tab === 'leaderboard' ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          {/* Left: leaderboard */}
          <div className="lg:col-span-3 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Trophy className="h-4 w-4 text-warning-text" /> {t('loyaltyLeaderboardTitle')}
                  </CardTitle>
                  <div className="relative w-56">
                    <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      ref={searchRef}
                      placeholder={t('loyaltySearchCustomers')}
                      value={customerSearch}
                      onChange={e => { setCustomerSearch(e.target.value); setLbPage(1); }}
                      className="h-8 ps-8 pe-7 text-sm"
                    />
                    {customerSearch && (
                      <button onClick={() => setCustomerSearch('')} className="absolute end-2 top-1/2 -translate-y-1/2">
                        <X className="h-3 w-3 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {lbLoading ? <SkeletonTable rows={8} cols={3} /> : customers.length === 0 ? (
                  <EmptyState icon={Users} title={t('loyaltyNoCustomers')} description={t('loyaltyNoCustomersDesc')} />
                ) : (
                  <div className="space-y-1">
                    {customers.map((c: LoyaltyCustomer, i: number) => (
                      <button
                        key={c.id}
                        onClick={() => { setSelectedCustomer(c); setHistPage(1); setShowAdjust(false); }}
                        className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-start transition-all hover:bg-accent/60 ${selectedCustomer?.id === c.id ? 'bg-primary/10 border border-primary/30' : ''}`}
                      >
                        <div className="flex w-6 items-center justify-center shrink-0">
                          {rankIcon((lbPage - 1) * 20 + i)}
                        </div>
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 text-xs font-bold text-primary-foreground">
                          {c.name?.charAt(0)?.toUpperCase() ?? '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{c.name}</p>
                          {c.phone && <p className="text-xs text-muted-foreground">{c.phone}</p>}
                        </div>
                        <div className="text-end shrink-0">
                          <p className={`text-sm font-bold ${(c.loyaltyPoints ?? 0) > 0 ? 'text-warning-text' : 'text-muted-foreground'}`}>
                            {(c.loyaltyPoints ?? 0).toLocaleString()} {t('loyaltyPtsLabel')}
                          </p>
                          <p className="text-xs text-muted-foreground">{formatCurrency((c.loyaltyPoints ?? 0) * redeemRate)} {t('loyaltyValLabel')}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-3">
                  <PaginationBar page={lbPage} pageSize={20} total={lbTotal} onPageChange={setLbPage} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: selected customer detail */}
          <div className="lg:col-span-2">
            {!selectedCustomer ? (
              <Card className="h-full flex items-center justify-center">
                <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                    <Users className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium">{t('loyaltySelectCustomer')}</p>
                  <p className="text-xs text-muted-foreground">{t('loyaltySelectCustomerDesc')}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Customer header */}
                <Card>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 text-lg font-bold text-primary-foreground shrink-0">
                        {selectedCustomer.name?.charAt(0)?.toUpperCase() ?? '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold truncate">{selectedCustomer.name}</p>
                          {customerTier && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold border"
                              style={{ borderColor: customerTier.color, color: customerTier.color, background: `${customerTier.color}18` }}
                            >
                              {customerTier.badge} {customerTier.name}
                            </span>
                          )}
                        </div>
                        {selectedCustomer.phone && <p className="text-xs text-muted-foreground">{selectedCustomer.phone}</p>}
                        <div className="mt-2 flex items-center gap-3">
                          <div className="rounded-lg bg-warning/10 border border-warning/30 px-3 py-1.5 text-center">
                            <p className="text-lg font-bold text-warning-text">{(selectedCustomer.loyaltyPoints ?? 0).toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">{t('loyaltyPointsLabel')}</p>
                          </div>
                          <div className="rounded-lg bg-success/10 border border-success/30 px-3 py-1.5 text-center">
                            <p className="text-lg font-bold text-success-text">{formatCurrency((selectedCustomer.loyaltyPoints ?? 0) * redeemRate)}</p>
                            <p className="text-xs text-muted-foreground">{t('loyaltyCashValueLabel')}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Adjust points */}
                    {canManage && (
                      <div className="mt-4">
                        {!showAdjust ? (
                          <Button size="sm" variant="outline" className="w-full" onClick={() => setShowAdjust(true)}>
                            <Settings2 className="h-3.5 w-3.5 me-1.5" /> {t('loyaltyAdjustPoints')}
                          </Button>
                        ) : (
                          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">{t('loyaltyAdjustPointsDesc')}</p>
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                placeholder={t('loyaltyAdjustPlaceholder')}
                                value={adjustPoints}
                                onChange={e => setAdjustPoints(e.target.value)}
                                className="h-8 text-sm"
                              />
                              <Button
                                size="sm"
                                onClick={() => { if (!adjustPoints || isNaN(parseInt(adjustPoints))) { toast.error(t('loyaltyInvalidAdjust')); return; } adjustMutation.mutate(); }}
                                disabled={!adjustPoints || adjustMutation.isPending}
                                className="shrink-0"
                              >
                                {adjustMutation.isPending ? '...' : t('loyaltyApply')}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setShowAdjust(false)} className="shrink-0 px-2">
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                            <Input
                              placeholder={t('loyaltyReasonPlaceholder')}
                              value={adjustReason}
                              onChange={e => setAdjustReason(e.target.value)}
                              className="h-8 text-sm"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Transaction history */}
                <Card>
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <History className="h-4 w-4" /> {t('loyaltyTransactionHistory')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4">
                    {histLoading ? <SkeletonTable rows={5} cols={3} /> : history.length === 0 ? (
                      <EmptyState icon={History} title={t('loyaltyNoHistory')} description={t('loyaltyNoHistoryDesc')} />
                    ) : (
                      <>
                        <Table mobileCards className="text-xs">
                          <THead>
                            <Tr hover={false}>
                              <Th>{t('loyaltyColDate')}</Th>
                              <Th>{t('loyaltyColType')}</Th>
                              <Th className="text-end">{t('loyaltyColPts')}</Th>
                              <Th className="text-end">{t('loyaltyColBal')}</Th>
                              <Th>{t('loyaltyColReason', { defaultValue: 'Reason / invoice' })}</Th>
                            </Tr>
                          </THead>
                          <TBody>
                            {history.map((h: LoyaltyHistoryEntry) => (
                              <Tr key={h.id}>
                                <Td label={t('loyaltyColDate')} className="text-muted-foreground">{new Date(h.createdAt).toLocaleDateString()}</Td>
                                <Td label={t('loyaltyColType')}>
                                  <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 font-medium ${
                                    h.type === 'EARN' ? 'bg-success/10 text-success-text ' :
                                    h.type === 'REDEEM' ? 'bg-info/10 text-info-text ' :
                                    h.type === 'RETURN' ? 'bg-destructive/10 text-destructive-text ' :
                                    h.type === 'EXPIRE' ? 'bg-muted text-muted-foreground' :
                                    'bg-warning/10 text-warning-text '
                                  }`}>
                                    {h.type}
                                  </span>
                                </Td>
                                <Td label={t('loyaltyColPts')} className={`text-end font-semibold ${h.points >= 0 ? 'text-success-text' : 'text-destructive-text'}`}>
                                  {h.points >= 0 ? '+' : ''}{h.points}
                                </Td>
                                <Td label={t('loyaltyColBal')} className="text-end font-medium">{h.balanceAfter}</Td>
                                <Td label={t('loyaltyColReason', { defaultValue: 'Reason / invoice' })} className="text-muted-foreground">
                                  {h.sale?.invoiceNumber ? (
                                    <>
                                      <RecordLink type="invoice" id={h.sale.id} label={h.sale.invoiceNumber} variant="mono" className="text-xs" />
                                      {h.notes ? ` · ${h.notes}` : null}
                                    </>
                                  ) : (
                                    h.notes || '—'
                                  )}
                                </Td>
                              </Tr>
                            ))}
                          </TBody>
                        </Table>
                        <div className="mt-2">
                          <PaginationBar page={histPage} pageSize={15} total={histTotal} onPageChange={setHistPage} />
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ── Settings tab ── */
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <SlidersHorizontal className="h-4 w-4" /> {t('loyaltyProgramSettings')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {settingsLoading ? <SkeletonTable rows={4} cols={2} /> : settingsForm && (
              <div className="space-y-6">
                {/* Enable toggle */}
                <div className="flex items-center justify-between rounded-xl border bg-muted/30 p-4">
                  <div>
                    <p className="font-medium text-sm">{t('loyaltyEnableProgram')}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('loyaltyEnableProgramDesc')}</p>
                  </div>
                  <button
                    onClick={() => setSettingsForm((f) => (f ? { ...f, isEnabled: !f.isEnabled } : f))}
                    className={`relative h-6 w-11 rounded-full transition-colors ${settingsForm.isEnabled ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                  >
                    <span className={`absolute top-0.5 start-0.5 h-5 w-5 rounded-full bg-background shadow transition-transform ${settingsForm.isEnabled ? 'translate-x-5 rtl:-translate-x-5' : ''}`} />
                  </button>
                </div>

                {/* Earn rate */}
                <div className="rounded-xl border p-4 space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-success-text" /> {t('loyaltyEarnRate')}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">{t('loyaltyPointsEarned')}</Label>
                      <Input type="number" min="0.01" step="0.01" value={settingsForm.earnRatePoints}
                        onChange={e => patchForm({ earnRatePoints: e.target.value })} className="mt-1" />
                    </div>
                    <span className="text-muted-foreground mt-5">{t('loyaltyPtsPerEvery')}</span>
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">{t('loyaltyPkrSpent')}</Label>
                      <Input type="number" min="1" value={settingsForm.earnRatePKR}
                        onChange={e => patchForm({ earnRatePKR: e.target.value })} className="mt-1" />
                    </div>
                    <span className="text-muted-foreground mt-5">{t('loyaltyPkrLabel')}</span>
                  </div>
                  <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
                    {t('loyaltyEarnExample', { points: Math.floor(500 / Number(settingsForm.earnRatePKR || 10)) * Number(settingsForm.earnRatePoints || 1) })}
                  </p>
                </div>

                {/* Redeem rate */}
                <div className="rounded-xl border p-4 space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2"><Gift className="h-4 w-4 text-destructive-text" /> {t('loyaltyRedeemRate')}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{t('loyalty1PointEquals')}</span>
                    <Input type="number" min="0.001" step="0.001" value={settingsForm.redeemRate}
                      onChange={e => patchForm({ redeemRate: e.target.value })} className="w-28" />
                    <span className="text-muted-foreground">{t('loyaltyPkrLabel')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{t('loyaltyMinRedeem')}</span>
                    <Input type="number" min="1" value={settingsForm.minRedeemPoints}
                      onChange={e => patchForm({ minRedeemPoints: e.target.value })} className="w-28" />
                    <span className="text-muted-foreground">{t('loyaltyMinPts')}</span>
                  </div>
                  <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
                    {t('loyaltyRedeemExample', { value: formatCurrency(100 * Number(settingsForm.redeemRate || 0.01)) })}
                  </p>
                </div>

                {/* Expiry */}
                <div className="rounded-xl border p-4 space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2"><History className="h-4 w-4 text-warning-text" /> {t('loyaltyPointsExpiry')}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <Input type="number" min="1" value={settingsForm.expiryDays}
                      onChange={e => patchForm({ expiryDays: e.target.value })}
                      placeholder={t('loyaltyExpiryPlaceholder')} className="w-36" />
                    <span className="text-muted-foreground">{t('loyaltyExpiryDays')}</span>
                  </div>
                </div>

                {/* Membership Tiers */}
                <div className="rounded-xl border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-warning-text" /> Membership Tiers
                    </p>
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => {
                        const tiers = settingsForm.tiers ?? [];
                        patchForm({
                          tiers: [...tiers, { name: 'New Tier', minPoints: 0, multiplier: 1, badge: '⭐', color: '#6366f1' }],
                        });
                      }}
                    >
                      <Plus className="h-3.5 w-3.5 me-1" /> Add Tier
                    </Button>
                  </div>
                  {(!settingsForm.tiers || settingsForm.tiers.length === 0) && (
                    <div className="flex items-center gap-3 rounded-lg border border-dashed p-3">
                      <Trophy className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div>
                        <p className="text-sm font-medium">No tiers configured</p>
                        <p className="text-xs text-muted-foreground">Add Bronze/Silver/Gold tiers to reward loyal customers.</p>
                      </div>
                      <Button
                        type="button" size="sm" variant="secondary" className="ms-auto shrink-0"
                        onClick={() => patchForm({ tiers: DEFAULT_TIERS })}
                      >
                        Use Defaults
                      </Button>
                    </div>
                  )}
                  <div className="space-y-2">
                    {(settingsForm.tiers ?? []).map((tier: LoyaltyTier, idx: number) => (
                      <div key={idx} className="grid grid-cols-[1fr_1fr_auto_auto_auto] gap-2 items-center rounded-lg border bg-muted/20 p-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Name</Label>
                          <Input
                            value={tier.name}
                            onChange={e => patchTier(idx, { name: e.target.value })}
                            className="h-8 text-sm mt-0.5"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Min Points Earned</Label>
                          <Input
                            type="number" min="0" step="100"
                            value={tier.minPoints}
                            onChange={e => patchTier(idx, { minPoints: Number(e.target.value) })}
                            className="h-8 text-sm mt-0.5"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Multiplier</Label>
                          <Input
                            type="number" min="0.1" step="0.1"
                            value={tier.multiplier}
                            onChange={e => patchTier(idx, { multiplier: Number(e.target.value) })}
                            className="h-8 text-sm mt-0.5 w-20"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Badge</Label>
                          <Input
                            value={tier.badge}
                            onChange={e => patchTier(idx, { badge: e.target.value })}
                            className="h-8 text-sm mt-0.5 w-16"
                          />
                        </div>
                        <div className="mt-4">
                          <Button
                            type="button" variant="ghost" size="sm"
                            onClick={() => setSettingsForm((f) => (f ? {
                              ...f,
                              tiers: (f.tiers ?? []).filter((_, i) => i !== idx),
                            } : f))}
                            className="text-destructive-text hover:text-destructive-text px-2"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Referral Program */}
                <div className="rounded-xl border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold flex items-center gap-2">
                      <span className="text-info-text">🔗</span> Referral Program
                    </p>
                    <Label className="flex items-center gap-2 cursor-pointer select-none font-normal leading-normal text-foreground">
                      <Checkbox
                        checked={settingsForm.referralEnabled ?? false}
                        onChange={e => patchForm({ referralEnabled: e.target.checked })}
                        className="accent-info"
                      />
                      <span className="text-sm font-medium">{settingsForm.referralEnabled ? 'Enabled' : 'Disabled'}</span>
                    </Label>
                  </div>
                  {settingsForm.referralEnabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Referrer Bonus (points)</Label>
                        <div className="flex items-center gap-2 text-sm">
                          <Input type="number" min="0" value={settingsForm.referralBonusReferrer ?? 50}
                            onChange={e => patchForm({ referralBonusReferrer: e.target.value })}
                            className="w-24" />
                          <span className="text-xs text-muted-foreground">pts to referrer</span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Referee Signup Bonus (points)</Label>
                        <div className="flex items-center gap-2 text-sm">
                          <Input type="number" min="0" value={settingsForm.referralBonusReferee ?? 25}
                            onChange={e => patchForm({ referralBonusReferee: e.target.value })}
                            className="w-24" />
                          <span className="text-xs text-muted-foreground">pts to new customer</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">When enabled, every new customer who signs up with a referral code earns bonus points. The referrer also receives a loyalty bonus.</p>
                </div>

                {canManage && (
                  <Button
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending}
                    variant="gradient"
                  >
                    <Save className="h-4 w-4 me-2" />
                    {saveMutation.isPending ? t('loyaltySaving') : t('loyaltySaveSettings')}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
